import React, { useEffect, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/webvideo/UserPublicDataProvider";
import { useTranslation } from "locales";
import { getProduceStreams } from "lib/liveStreamHelpers";
import { MediaConstraints, ProduceStream, ProduceUser } from "types";
import ChatTimer from "components/ChatTimer";
import DotFlashingAnimation from "components/DotFlashingAnimation";
import StreamView from "components/StreamView";
import useViewController from "./viewController";
import EndAppointmentScreen from "../EndAppointmentScreen";

interface Props {
  produceUser: UserPublicData;
  threadId: string | undefined;
  loading: boolean;
  userList: LiveChatUser[];
  streamStartedDate: string;
  token: string;
  audioInputs?: MediaDeviceInfo[];
  videoInputs?: MediaDeviceInfo[];
  managedConstraints: MediaConstraints | null;
  onChangeConstraints?: (constraints: MediaConstraints) => void;
  me: ProduceUser | null;
  users: ProduceUser[];
  streamEndedDate: string;
  onCloseStream: () => void;
  slotControlButtons?: JSX.Element;
  isProducerFinishedStream: boolean;
  onProduceFinishedStream: () => Promise<void>;
}

export default function AppointmentStreamView({
  produceUser,
  threadId,
  loading,
  userList,
  token,
  audioInputs,
  videoInputs,
  managedConstraints,
  onChangeConstraints,
  me,
  users,
  streamStartedDate,
  streamEndedDate,
  onCloseStream,
  slotControlButtons,
  isProducerFinishedStream,
  onProduceFinishedStream,
}: Props) {
  const { t } = useTranslation();
  const { isChatEnded, onCloseChat, isPerformer } = useViewController(
    streamEndedDate,
    userList,
    onCloseStream,
    onProduceFinishedStream
  );

  const [streams, setStreams] = useState<ProduceStream[]>([]);

  useEffect(() => {
    const result: ProduceStream[] = [];
    if (me) {
      result.push(...getProduceStreams(me));
    }
    users.forEach((user) => result.push(...getProduceStreams(user)));
    setStreams(result);
  }, [me, users]);

  const slotVideoScreen = (() => {
    if (isChatEnded || isProducerFinishedStream) {
      return <EndAppointmentScreen produceUser={produceUser} key="end-screen" />;
    } else if (userList.length < 2) {
      return <DotFlashingAnimation key="dot-animation" />;
    }
  })();

  const slotContentConfirmationExit = (
    <>
      <h1 className="headline-1 text-center mb-6">{t("Do you want to end the appointment?")}</h1>
      <p className="body text-center">{t("The appointment cannot be resumed after completion.")}</p>
    </>
  );

  const slotTimer = (
    <ChatTimer
      hideTimer={!isPerformer || isChatEnded}
      streamStartedDate={streamStartedDate}
      grayRecordIcon={isChatEnded}
    >
      {t("Private chat")}
    </ChatTimer>
  );

  return (
    <StreamView
      produceUser={produceUser}
      threadId={threadId}
      produce
      loading={loading}
      userList={userList}
      token={token}
      managedConstraints={managedConstraints}
      audioInputs={audioInputs}
      videoInputs={videoInputs}
      onChangeConstraints={onChangeConstraints}
      showConfirmationExit
      streams={streams}
      me={me}
      slotControlButtons={slotControlButtons}
      forceHideControlButtons={isChatEnded || isProducerFinishedStream}
      onClose={onCloseChat}
      disableUserList
      slotTimer={slotTimer}
      slotVideoScreen={slotVideoScreen}
      slotContentConfirmationExit={slotContentConfirmationExit}
      hideChatOnStart
    />
  );
}
